import {
  OrderMode,
  OrderPaymentStatus,
  OrderPaymentType,
  OrderStatus,
  OrderType,
} from '../enums/OrderEnums';
import { RoundMode } from '@/enums/RoundMode';
import { PaymentMethod } from '@/enums/PaymentMethod';
import { TaxDisplayMode } from '@/enums/TaxDisplayMode';
import { IOrder } from '@/interfaces/IOrder';
import { applyRound, roundPrice, round2 } from '@/utils';
import { IDeliveryInfo } from '@/interfaces/IDeliveryInfo';
import { ICart, ICartItem } from '@/interfaces/ICart';
import { IDeliveryZone } from '@/interfaces/IDeliveryZone';
import { getContainingDeliveryZone } from '@/utils/polygon.utils';
import { IBranch } from '@/interfaces/IBranch';
import { IRestaurantSettings } from '@/api/services/restaurants.service';
import { IItem } from '@/interfaces/IItem';
import { IOption } from '@/interfaces/IOption';
import { IOrderField } from '@/interfaces/IOrderField';
import { IOrderItem } from '@/interfaces/IOrderItem';
import { IOrderItemOption } from '@/interfaces/IOrderItemOption';
import { IRestaurant } from '@/interfaces/IRestaurant';
import { ITable } from '@/interfaces/ITable';
import { IUser } from '@/interfaces/IUser';
import ICustomField from '@/interfaces/ICustomField';

/**
 * This class provides utility methods for creating and manipulating orders on the client-side.
 * It mirrors the server-side logic for order creation, ensuring that the client can generate
 * a complete order object even when offline.
 */
class ClientOrderUtils {
  /**
   * Calculates the price of an item based on its options.
   *
   * @param {IItem} item - The item to calculate the price for.
   * @param {Record<string, Record<string, number>>} optionValues - The selected option values.
   * @returns {number} The calculated item price.
   */
  static calculateItemPrice({
    item,
    optionValues,
  }: {
    item: IItem;
    optionValues: Record<string, Record<string, number>>;
  }): number {
    const itemOptions: IOption[] = this.itemToItemOptions(item);

    const basePrice = item.discountedPrice ?? item.price;

    let itemPrice = basePrice;

    let optionsPrice = 0,
      variantPrice: null | number = null;

    for (const key in optionValues) {
      const optionId = parseInt(key);
      const itemOption = itemOptions.find((o) => o.id === optionId);

      if (!itemOption) {
        console.warn(
          `Option ${optionId} not found for item ${item.id} (${item.name})`
        );
        continue;
      }

      const value = optionValues[optionId];

      for (const valueKey in value) {
        const optionItemId = parseInt(valueKey);
        const optionItemValue = value[optionItemId];

        if (optionItemValue === 0) {
          continue;
        }

        const optionItem = itemOption?.optionItems?.find(
          (oi) => oi.id === optionItemId
        );

        if (!optionItem) {
          console.warn(
            `Option item ${optionItemId} not found for option ${optionId}`
          );
          continue;
        }

        const optionItemPrice = optionItem.discountedCost ?? optionItem.cost;
        const optionItemTotalPrice = optionItemPrice * optionItemValue;

        if (itemOption.main) {
          variantPrice = optionItemTotalPrice;
        } else {
          optionsPrice += optionItemTotalPrice;
        }
      }
    }

    if (variantPrice !== null) {
      itemPrice = variantPrice;
    }

    itemPrice += optionsPrice;

    return itemPrice;
  }

  /**
   * Converts an item to an array of options, including both item options and variant options.
   *
   * @param {IItem} item - The item to convert.
   * @returns {IOption[]} An array of options.
   */
  static itemToItemOptions(item: IItem): IOption[] {
    const itemOptions: IOption[] = [];

    if (item.options) {
      // Add Price variants
      itemOptions.push(...item.options);
    }

    if (item.itemOptions) {
      // Add Opcionales y Adicionales
      for (const iO of item.itemOptions) {
        if (!iO.option) {
          console.warn('Item option is missing');
          continue;
        }

        const optionItems = iO.option.optionItems;
        const optionWithoutOptionItems = { ...iO.option };
        delete optionWithoutOptionItems.optionItems;

        itemOptions.push({
          ...optionWithoutOptionItems,
          optionItems: optionItems.map((oi) => ({
            ...oi,
            option: iO.option,
          })),
          mandatory: iO.mandatory ?? iO.option.mandatory,
          multiple: iO.multiple ?? iO.option.multiple,
          min: iO.min ?? iO.option.min,
          max: iO.max ?? iO.option.max,
          active: iO.active ?? iO.option.active,
        });
      }
    }

    return itemOptions;
  }

  /**
   * Applies tax to a price based on restaurant settings.
   *
   * @param {object} params - The parameters for applying tax.
   * @param {IRestaurant} params.restaurant - The restaurant.
   * @param {IRestaurantSettings} params.settings - The restaurant settings.
   * @param {number} params.price - The price to apply tax to.
   * @param {boolean} params.partial - Whether to apply tax partially.
   * @returns {number} The price with tax applied.
   */
  static applyTax({
    restaurant,
    settings,
    price,
    partial,
  }: {
    restaurant: IRestaurant;
    settings: IRestaurantSettings;
    price: number;
    partial: boolean;
  }): number {
    if (
      !restaurant?.tax ||
      !settings ||
      settings.taxMode === TaxDisplayMode.NoTax
    )
      return price;

    if (partial) {
      if (
        settings.taxMode === TaxDisplayMode.AddAndShow ||
        settings.taxMode === TaxDisplayMode.SilentAdd
      ) {
        return price * (1 + restaurant.tax / 100);
      }
    } else {
      if (settings.taxMode === TaxDisplayMode.AddAtInvoice) {
        return price * (1 + restaurant.tax / 100);
      }
    }

    return price;
  }

  /**
   * Calculates the total cost of an order, including products, delivery, and taxes.
   *
   * @param {object} params - The parameters for calculating the order total.
   * @param {IRestaurant} params.restaurant - The restaurant.
   * @param {IBranch} params.branch - The branch.
   * @param {IRestaurantSettings} params.settings - The restaurant settings.
   * @param {ICart} params.cart - The cart.
   * @param {OrderType} params.orderType - The order type.
   * @param {IDeliveryInfo | null} params.deliveryInfo - The delivery info.
   * @param {IDeliveryZone[] | null} params.deliveryZones - The delivery zones.
   * @returns {{ products: number, productsTax: number, delivery: number, deliveryTax: number }} The order totals.
   */
  static calculateOrderTotals({
    restaurant,
    branch,
    settings,
    cart,
    orderType,
    deliveryInfo,
    deliveryZones,
  }: {
    restaurant: IRestaurant;
    branch: IBranch;
    settings: IRestaurantSettings;
    cart: ICart;
    orderType: OrderType;
    deliveryInfo: IDeliveryInfo | null;
    deliveryZones: IDeliveryZone[] | null;
  }): {
    products: number;
    productsTax: number;
    delivery: number;
    deliveryTax: number;
  } {
    const costs = {
      products: 0,
      productsTax: 0,
      delivery: 0,
      deliveryTax: 0,
    };

    if (orderType === OrderType.Delivery) {
      costs.delivery = branch.deliveryCost ?? 0;

      if (deliveryInfo && branch.zonedDelivery && branch.zonedFee) {
        if (!deliveryZones) {
          console.warn('Delivery zones not provided');
        } else {
          const deliveryZone = getContainingDeliveryZone(
            [deliveryInfo.lat!, deliveryInfo.lng!],
            deliveryZones
          );

          if (!deliveryZone) {
            console.warn('Delivery zone not found');
          } else {
            costs.delivery = deliveryZone.cost ?? costs.delivery;
          }
        }
      }

      if (settings.taxDelivery) {
        const taxedDelivery = this.applyTax({
          restaurant,
          settings,
          price: costs.delivery,
          partial: true,
        });

        if (settings.taxMode === TaxDisplayMode.AddAtInvoice) {
          costs.deliveryTax = taxedDelivery - costs.delivery;
        } else {
          costs.delivery = taxedDelivery;
        }
      }
    }

    for (const cartItem of cart.cartItems) {
      const itemCost = this.calculateItemPrice({
        item: cartItem.item!,
        optionValues: cartItem.optionValues,
      });

      const taxedItemCost = this.applyTax({
        restaurant,
        settings,
        price: itemCost,
        partial: true,
      });

      const tax = taxedItemCost - itemCost;

      costs.productsTax += tax * cartItem.quantity;

      if (settings.taxMode === TaxDisplayMode.AddAtInvoice) {
        costs.products += itemCost * cartItem.quantity;
      } else {
        costs.products += taxedItemCost * cartItem.quantity;
      }
    }

    return costs;
  }

  /**
   * Generates a complete order object based on the provided data.
   *
   * @param {object} params - The parameters for creating the order.
   * @param {IRestaurant} params.restaurant - The restaurant.
   * @param {IBranch} params.branch - The branch.
   * @param {ITable | null} params.table - The table.
   * @param {IRestaurantSettings} params.settings - The restaurant settings.
   * @param {OrderType} params.orderType - The order type.
   * @param {ICart} params.cart - The cart.
   * @param {IDeliveryInfo | null} params.deliveryInfo - The delivery info.
   * @param {PaymentMethod | null} params.paymentMethod - The payment method.
   * @param {{ name: string, phone: string }} params.customerData - The customer data.
   * @param {Record<string, string> | null} params.customFieldValues - The custom field values.
   * @param {IDeliveryZone[] | null} params.deliveryZones - The delivery zones.
   * @param {string | null} params.deviceId - The device ID.
   * @param {string | null} params.localId - The local ID.
   * @returns {IOrder} The generated order object.
   */
  static createOrder({
    restaurant,
    branch,
    table,
    settings,
    customFields,
    orderType,
    cart,
    deliveryInfo,
    paymentMethod,
    customerData,
    customFieldValues,
    deliveryZones,
    deviceId,
    localId,
  }: {
    restaurant: IRestaurant;
    branch: IBranch;
    table: ITable | null;
    settings: IRestaurantSettings;
    customFields: ICustomField[] | null;
    orderType: OrderType;
    cart: ICart;
    deliveryInfo: IDeliveryInfo | null;
    paymentMethod: PaymentMethod | null;
    customerData: { name: string; phone: string };
    customFieldValues: Record<string, string> | null;
    deliveryZones: IDeliveryZone[] | null;
    deviceId: string | null;
    localId?: string;
  }): IOrder {
    const now = new Date();

    const order: IOrder = {
      id: -Date.now(), // Temporary ID, will be replaced by server
      restaurantId: restaurant.id,
      restaurant,
      branchId: branch.id,
      branch,
      userId: 0, // User ID not available offline, will be set by server
      user: {} as IUser, // User not available offline, will be set by server
      tableId: table?.id || 0,
      table,
      mode: branch.ordersMode,
      humanId:
        orderType === OrderType.Away
          ? '' + (Math.floor(Math.random() * 900) + 100)
          : undefined, // 3-digit humanId for takeaway
      userWhatsapp: customerData.phone,
      userTel: customerData.phone,
      userName: customerData.name,
      userAddress: deliveryInfo?.address,
      userAddress2: deliveryInfo?.address2,
      userAddressComments: deliveryInfo?.addressComment,
      deliveryLat: deliveryInfo?.lat,
      deliveryLng: deliveryInfo?.lng,
      comment: cart.userComment,
      orderItems: [],
      orderFields: [],
      currency: restaurant.currency,
      subtotal: 0,
      discount: 0,
      discountAmount: 0,
      total: 0,
      totalPaid: 0,
      deliveryCost: 0,
      tax: restaurant.tax,
      taxAmount: 0,
      taxName: restaurant.taxName,
      taxMode: settings.taxMode,
      roundMode: settings.roundMode,
      roundDifference: 0,
      type: orderType,
      status: OrderStatus.Progress, // Default status for new offline orders
      paymentType:
        paymentMethod === PaymentMethod.Online
          ? OrderPaymentType.Online
          : OrderPaymentType.Store,
      paymentStatus: OrderPaymentStatus.Awaiting, // Default payment status for new orders
      paymentMethod: paymentMethod || PaymentMethod.Cash, // Default to Cash if not provided
      manual: true, // Assuming manual order creation when offline
      archived: false,
      paymentsByType: [],
      invoicingItemId: null,
      sessionId: null,
      createdAt: now,
      updatedAt: now,
      localUpdating: false,
      deviceId: deviceId,
      // Orders are born unstamped; the server assigns caja at first money event
      cajaId: null,
      cajaStatusId: null,
      localId: localId ?? null,
    };

    const totals = this.calculateOrderTotals({
      restaurant,
      branch,
      settings,
      cart,
      orderType,
      deliveryInfo,
      deliveryZones,
    });

    order.subtotal = totals.products;
    order.deliveryCost = totals.delivery;
    order.taxAmount = roundPrice(totals.productsTax + totals.deliveryTax);

    order.total = roundPrice(
      order.subtotal +
        totals.delivery +
        (settings.taxMode === TaxDisplayMode.AddAtInvoice
          ? order.taxAmount
          : 0) -
        (order.discountAmount ?? 0)
    );

    const roundedTotal = applyRound(
      order.total,
      settings.roundMode,
      settings.roundInterval
    );

    if (settings.roundMode !== RoundMode.None) {
      order.roundDifference = roundPrice(roundedTotal - order.total);
    }

    order.total = roundedTotal;

    if (customFieldValues) {
      for (const fieldId in customFieldValues) {
        order.orderFields!.push({
          orderId: order.id,
          fieldId: parseInt(fieldId),
          field: customFields?.find((f) => f.id === parseInt(fieldId)),
          value: customFieldValues[fieldId],
        } as IOrderField);
      }
    }

    let orderItemIndex = 0;
    for (const cartItem of cart.cartItems) {
      const orderItem: IOrderItem = {
        orderId: order.id,
        itemId: cartItem.itemId,
        item: cartItem.item,
        index: orderItemIndex++,
        price: roundPrice(
          this.applyTax({
            restaurant,
            settings,
            price: this.calculateItemPrice({
              item: cartItem.item!,
              optionValues: cartItem.optionValues,
            }),
            partial: true,
          })
        ),
        amount: cartItem.quantity,
        total: 0,
        comment: cartItem.userComment,
        manual: false,
        printT: 0,
        printC: 0,
        printE: 0,
        canceledAt: null,
        addedAt: null,
        modifiedAt: null,
        options: [],
      };

      orderItem.total = roundPrice(orderItem.price * orderItem.amount);

      const itemOptions = this.itemToItemOptions(cartItem.item!);

      for (const optionKey in cartItem.optionValues) {
        const optionId = parseInt(optionKey);
        const option = itemOptions.find((o) => o.id === optionId);

        if (!option) {
          console.warn(
            `Option ${optionId} not found for item ${cartItem.itemId} (${
              cartItem.item!.name
            })`
          );
          continue;
        }

        for (const optionItemKey in cartItem.optionValues[optionKey]) {
          const optionItemId = parseInt(optionItemKey);
          const optionItem = option.optionItems?.find(
            (oi) => oi.id === optionItemId
          );

          const val = cartItem.optionValues[optionKey][optionItemKey];

          if (!optionItem) {
            console.warn(
              `Option item ${optionItemId} not found for option ${optionId}`
            );
            continue;
          }

          if (!val) {
            continue;
          }

          const orderItemOption: IOrderItemOption = {
            orderId: order.id,
            itemId: cartItem.itemId,
            item: cartItem.item,
            index: orderItem.index,
            optionItemId: optionItemId,
            optionItem: optionItem,
            val: val,
            price: this.applyTax({
              restaurant,
              settings,
              price: optionItem.discountedCost ?? optionItem.cost,
              partial: true,
            }),
            total: 0,
          };

          orderItemOption.total = roundPrice(
            orderItemOption.price * orderItemOption.val
          );

          orderItem.options!.push(orderItemOption);
        }
      }

      order.orderItems!.push(orderItem);
    }

    return order;
  }
}

/**
 * Creates a complete order object on the client-side, mirroring the server-side logic.
 *
 * @param {object} params - The parameters for creating the order.
 * @param {IRestaurant} params.restaurant - The restaurant.
 * @param {IBranch} params.branch - The branch.
 * @param {ITable | null} params.table - The table.
 * @param {IRestaurantSettings} params.settings - The restaurant settings.
 * @param {OrderType} params.orderType - The order type.
 * @param {ICart} params.cart - The cart.
 * @param {IDeliveryInfo | null} params.deliveryInfo - The delivery info.
 * @param {PaymentMethod | null} params.paymentMethod - The payment method.
 * @param {{ name: string, phone: string }} params.customerData - The customer data.
 * @param {Record<string, string> | null} params.customFieldValues - The custom field values.
 * @param {IDeliveryZone[] | null} params.deliveryZones - The delivery zones.
 * @param {string | null} params.deviceId - The device ID.
 * @param {string | null} params.localId - The local ID.
 * @returns {IOrder} The generated order object.
 */
export function createClientOrder({
  restaurant,
  branch,
  table,
  settings,
  customFields,
  orderType,
  cart,
  deliveryInfo,
  paymentMethod,
  customerData,
  customFieldValues,
  deliveryZones,
  deviceId,
  localId,
}: {
  restaurant: IRestaurant;
  branch: IBranch;
  table: ITable | null;
  settings: IRestaurantSettings;
  customFields: ICustomField[] | null;
  orderType: OrderType;
  cart: ICart;
  deliveryInfo: IDeliveryInfo | null;
  paymentMethod: PaymentMethod | null;
  customerData: { name: string; phone: string };
  customFieldValues: Record<string, string> | null;
  deliveryZones: IDeliveryZone[] | null;
  deviceId: string | null;
  localId?: string;
}): IOrder {
  return ClientOrderUtils.createOrder({
    restaurant,
    branch,
    table,
    settings,
    customFields,
    orderType,
    cart,
    deliveryInfo,
    paymentMethod,
    customerData,
    customFieldValues,
    deliveryZones,
    deviceId,
    localId,
  });
}
